import React, { useState } from "react";

import { AddFilterBaseFunc, chartAttrs, defaultStyles, graphColors, IDimMDT, IFilterKeys, IFilters, IPlotData, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { PlotData } from "plotly.js";
import { Dialog } from "@mui/material";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { chartDimesionsCalc, ChartOrientation, ChartType, QoolDashChart } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";
import { StateLabel } from "config/appUrlConfig";

interface IProps {
	plotData: { [mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number } };
	mdtFacilityRef: IDimMDT[];
	activeFilters: IFilters<IFilterKeys>;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
	removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
	mdtName: string;
	cancerName: string;
}

/**
 * Reviews by service chart / My MDTs
 */
export function KMSurvivalChart(props: IProps) {
	const { plotData, mdtFacilityRef, mdtName, cancerName } = props;

	const chartTitle = "Kaplan-Meier Survival";


	let mdtSurvival: IPlotData = { labels: [], values: [] };
	let stateSurvival: IPlotData = { labels: [], values: [] };


	//Get MDTID from the MDT name
	let mdtId = mdtFacilityRef.find(x => x.nameAndFacility == mdtName)?.mdtNameId;
	const prefix = mdtId + cancerName + "|";

	//Extract labels (survival time)
	let labels: string[] = [];
	for (const key in plotData) {
		if (Object.prototype.hasOwnProperty.call(plotData, key)) {
			if (key.startsWith(prefix)) {
				labels.push(key.substring((key.indexOf("|") + 1)));
			}
		}
	}

	//Use labels to extract the values
	labels.forEach(a => {
		if (plotData[mdtId + cancerName + "|" + a]) {
			mdtSurvival.labels.push(a)
			mdtSurvival.values.push(plotData[mdtId + cancerName + "|" + a].mdtKMSurvival)

			stateSurvival.labels.push(a)
			stateSurvival.values.push(plotData[mdtId + cancerName + "|" + a].stateKMSurvival)
		}
	})

	const valuesMDT = mdtSurvival.values;
	const labelsMDT = mdtSurvival.labels;
	const mdt = { "name": "My MDT", "labels": labelsMDT, "values": valuesMDT };

	const valuesState = stateSurvival.values;
	const labelsState = stateSurvival.labels;
	const state = { "name": StateLabel, "labels": labelsState, "values": valuesState };


	const tracesArray: any[] = [];
	tracesArray.push(mdt);
	tracesArray.push(state);


	let survivalLabel: string[] = [];

	tracesArray?.forEach((a, i) => {

		//Ensure labels are read in as numbers then sort in ascending order 
		let xs: number[] = a.labels.map(x => {
			return Number(x);
		})
		xs.sort((a, b) => a - b);

		xs.forEach(x => {
			let years = Math.floor(x);
			let months = Math.round((x % 1) * 12)

			survivalLabel.push(years + " years, " + months + " months")
		})

	})

	const legend = (chartType: ChartType): JSX.Element => {

		if (chartType == ChartType.Table)
			return null;


		return (
			<div className="dash-legend">
				<div>
					<div className="key" style={{ backgroundColor: graphColors.default[0] }}></div>
					<div className="title">My MDT</div>
				</div>
				{state.values.length > 0 && <div>
					<div className="key" style={{ backgroundColor: graphColors.default[1] }}></div>
					<div className="title">{state.name}</div>
				</div>}
			</div>

		)
	}


	return (
		<QoolDashChart
			xAxis={{ horizontalOrientation: { label: "Years", style: { rangemode: "tozero", range: [0, 110] } }, verticalOrientation: { label: "Survival Rate (%)", style: { rangemode: "tozero", automargin: true, autotick: false, dtick: 1, title: "Years" } } }}
			yAxis={{ horizontalOrientation: { label: "Survival Rate (%)", style: { dtick: 1, autotick: false } }, verticalOrientation: { label: "Years", style: { rangemode: "tozero", range: [0, 110] } } }}
			chartOrder={{ horizontalOrientation: { reversed: false }, verticalOrientation: { reversed: false } }}
			defaultChartOrientation={ChartOrientation.Vertical}
			defaultChartType={ChartType.Line}
			chartTitle={chartTitle}
			filterKey={""}
			plotData={tracesArray}
			calculateRangeOnPlotDataIndex={0}
			activeFilters={props.activeFilters}
			addFilterFn={props.addFilterFn}
			removeFilterFn={props.removeFilterFn}
			filterFn={null}
			hoverText="%{fullData.name}"
			hovertemplate={"%{hovertext}<br>" +
				"%{fullData.name}: %{y:,}%<br>" +
				"<extra></extra>"}
			hoverTextAlt={survivalLabel}
			breadcrumb={null}
			chartIcons={{
				pie: false,
				line: true,
				horizontalBar: false,
				verticalBar: false,
				help: ["Overall survival is calculated from date of diagnosis to date of death"]
			}}
			chartDimensions={{
				chartHeightProportion: 50,
				parentColNumber: 12,
				tabColNumber: 6
			}}
			legend={legend}
			msgWhenHidden={"Please select a single MDT and Cancer to generate data"}
		/>
	)

}
